import { Controller } from 'stimulus';
import { clamp } from '../src/js/utils/utils';

class PageLoadingController extends Controller {
  static targets = ['bar', 'container'];

  initialize() {
    this.status = null;
    this.settings = {
      trickle: true,
      speed: 200,
      minimum: 0.5,
      maximum: 100,
    };
  }

  connect() {
    if (this.containerTarget) {
      this.fakeFinish();
    }
  }

  /**
   * Initializes progress bar. If cmd or ctrl keys pressed, does not continue.
   * If `this.status == null` (meaning the bar is not running) sets status to
   * either value passed in, or default value of 0. If progres bar can be shown,
   * initializes `work` timeout which continually invokes `this._increment` until
   * the max progress value has been met.
   * @param {Event} e If triggered by user, click event, else undefined
   * @param {Number} value Optional width value to initialize progress bar
   * @returns {void}
   */
  start(e, value = 0) {
    if (e.metaKey || e.ctrlKey) {
      return;
    }

    if (!this.status) {
      this._set(value);
    }

    if (!this._show()) {
      return;
    }

    this.work = () => {
      setTimeout(() => {
        if (!this.status) {
          return;
        }

        this._increment();
        this.work();
      }, clamp(Math.random() * 500, 200, 500));
    };

    if (this.settings.trickle) {
      this.work();
    }
  }

  /**
   * If the progress bar exists on the DOM,toggles .u-displayNone and sets
   * the bar transition style, returns true. Else returns false.
   * @returns {Boolean} Whether or not the progress bar was shown
   */
  _show() {
    if (this.containerTarget) {
      this.containerTarget.classList.remove('u-displayNone');
      this.barTarget.style.transition = 'all 200ms ease';

      return true;
    }

    return false;
  }

  _hide() {
    this.containerTarget?.classList.add('u-displayNone');
  }

  /**
   * Incrementally decrease the amount of progress width added to the Progress
   * Bar. While under 20%, adds 2.5%. While under 50%, adds 2%. While under 80%,
   * adds 1%. While under 100%, adds 0.5%. Invokes `this._set` with new amount.
   * @param {Undefined|Number} amount Undefined in most cases, otherwise 0-100
   * @returns {Void} Boolean, Null, or Undefined, return value not used.
   */
  // eslint-disable-next-line complexity
  _increment(amount) {
    let n = this.status;
    let inc = amount;

    if (!n) {
      return this.start();
    }

    if (n > this.settings.maximum) {
      return null;
    }

    if (typeof amount !== 'number') {
      if (n >= 0 && n < 20) {
        inc = 2.5;
      } else if (n >= 20 && n < 50) {
        inc = 2;
      } else if (n >= 50 && n < 80) {
        inc = 1;
      } else {
        inc = 0.5;
      }
    }

    n = clamp(n + inc, 0, 100);

    return this._set(n);
  }

  /**
   * Given a value (of 0-100), adds a step to the queue to set the Progress Bar
   * width. If value is 100, adds additional setTimeout step to invoke
   * `this._hide` and clear the Progress Bar.
   * @param {Number} value amount to set Progress Bar width
   * @returns {void}
   */
  _set(value) {
    let n = clamp(value, this.settings.minimum, this.settings.maximum);

    this.status = n === this.settings.maximum ? null : n;
    this._queue((next) => {
      this.containerTarget.ariaValueNow = n;
      this.barTarget.style.width = `${n}%`;

      if (n === this.settings.maximum) {
        setTimeout(() => {
          this.barTarget.style.transition = 'all 200ms linear';

          setTimeout(() => {
            this._hide();

            next();
          }, this.settings.speed);
        }, this.settings.speed);
      } else {
        setTimeout(next, this.settings.speed);
      }
    });
  }

  /**
   * Adds step to queue to force bar width to 100%. If `force` is provided as
   * True, will start by showing the bar if it's not already present.
   * @param {Boolean} force Whether or not to force show the Progress Bar before hiding.
   * @returns {void}
   */
  done(force) {
    if (!force && !this.status) {
      return;
    }

    this._show();
    this._queue((next) => {
      setTimeout(() => {
        this._set(this.settings.maximum);
        next();
      }, this.settings.speed);
    });
  }

  /**
   * Shows the progress bar and sets width to 75%, then invokes done. This is used
   * on page load to mimic a request being "finished".
   * @returns {void}
   */
  fakeFinish() {
    if (!this._show()) {
      return;
    }

    if (!this.status) {
      this._set(75);
    }

    this.done();
  }
}

/**
 * IIFE to handle performing multiple setTimeout invoked functions in order.
 * The result is a function which takes in a function to add to the queue, and
 * invokes each queued function in order after every invocation of the prior
 * function.
 */
PageLoadingController.prototype._queue = (() => {
  const pending = [];

  const next = () => {
    const fn = pending.shift();

    if (fn) {
      fn(next);
    }
  };

  // eslint-disable-next-line consistent-return
  return (fn) => {
    pending.push(fn);

    if (pending.length === 1) {
      return next();
    }
  };
})();

export default PageLoadingController;
